import { TripCommitment } from 'src/app/shared/model/reduction-action';

export const TIP_TYPES = [
  'transport_alternative',
  'direct_flight',
  'trip_duration',
  'eco_accommodation',
  'high_emission_activity',
] as const;

export type TipType = (typeof TIP_TYPES)[number];

/**
 * Stats category of the impact page a tip is shown under — the part of the
 * trip it would change. `trip_duration` changes no element, only the daily
 * intensity, so it sits with the eco-score that intensity drives.
 */
export type TipSection =
  | 'transport'
  | 'accommodation'
  | 'activities'
  | 'ecoScore';

export interface ReductionTip {
  id: string;
  type: TipType;
  /** True when the company committed to this reduction action. */
  isCommitted: boolean;
  data:
    | TransportAlternativeTipData
    | DirectFlightTipData
    | TripDurationTipData
    | EcoAccommodationTipData
    | HighEmissionActivityTipData;
}

/** Payload of GET /trips/:id/reduction-tips. */
export interface TripReductionTipsResponse {
  tips: ReductionTip[];
  commitments: TripCommitment[];
}

/**
 * The ground alternatives a flight can be compared against, in display order.
 *
 * `mixed` is a journey that changes family on the way — a TGV to Montpellier and
 * a FlixBus onwards. It is offered on its own rather than filed under the longer
 * half, because taking it means booking two different things.
 */
export const ALTERNATIVE_MODES = [
  'train',
  'bus',
  'ferry',
  'mixed',
  'carpooling',
] as const;

export type AlternativeMode = (typeof ALTERNATIVE_MODES)[number];

/**
 * The modes a company can allow or exclude. `mixed` is not among them: it is
 * offered whenever every vehicle it rides is allowed.
 */
export const SELECTABLE_ALTERNATIVE_MODES = [
  'train',
  'bus',
  'ferry',
  'carpooling',
] as const;

export type SelectableAlternativeMode =
  (typeof SELECTABLE_ALTERNATIVE_MODES)[number];

export interface TransportAlternativeTipData {
  transportId: string;
  transportTitle?: string;
  transportType: string;
  flightRoute?: string;
  transportDuration?: number;
  currentEmissionsPerPerson: number;
  /**
   * Distance of the flight itself, return included — not of the alternatives.
   *
   * The itineraries under `alternatives[mode].routes` are one-way; the
   * transport's own `return` flag says whether they are ridden twice. Every
   * emission figure follows this field's basis, not theirs.
   */
  distance: number;
  /**
   * What this flight could be replaced with, keyed by mode. A mode absent from
   * the map has no alternative — there is no itinerary for it, so the API had no
   * honest figure to give either. Never read a missing mode as zero.
   *
   * With `groundRoutes` set, every entry carries the `routes` it was priced on.
   * Without it, the map holds at most `train`, `bus` and `carpooling` — those
   * the company allows — estimated off the flight's own distance.
   */
  alternatives: Partial<Record<AlternativeMode, TransportAlternative>>;
  /**
   * Set when the flight's airports were known and Google Maps confirmed at least
   * one ground itinerary between their cities. Its presence is what says the
   * figures rest on real routes rather than on the flight's great-circle
   * distance.
   *
   * Absent means no airport code on the flight, or the API could not reach
   * Google.
   */
  groundRoutes?: TransportAlternativeRoutes;
}

export interface TransportAlternative {
  /**
   * Per person, on the same return basis as `distance`. With `routes`, these
   * are the figures of `routes[0]` — the itinerary offered by default.
   */
  emissionsPerPerson: number;
  savingsPercent: number;
  /**
   * The distinct itineraries Google confirmed for this mode, fastest first, each
   * priced on itself. Never empty when present; absent when the mode is an
   * estimate off the flight's distance.
   */
  routes?: TransportAlternativeRouteOption[];
}

export interface TransportAlternativeRouteOption {
  /** Per person, on the same return basis as `distance`. */
  emissionsPerPerson: number;
  savingsPercent: number;
  /**
   * The itinerary priced.
   *
   * Taking it means replacing the flight with **one transport per leg**, not
   * one transport of `route.distanceKm`: every leg was priced at its own mode,
   * so collapsing them would no longer add up to `emissionsPerPerson`.
   * Consecutive legs sharing a mode may be summed — same factor, so the total is
   * untouched. Legs of different modes may never be.
   */
  route: TransportAlternativeRoute;
}

/** What the user picked for a transport tip: a mode, and one of its `routes`. */
export interface AlternativeChoice {
  mode: AlternativeMode;
  /** Index into `routes` — 0 is the itinerary offered by default. */
  routeIndex: number;
}

/**
 * Where the selected tips are written: over the trip itself, or into a new
 * copy of it that leaves the original as it was — and takes a trip of the pack.
 */
export type ApplyTipsTarget = 'overwrite' | 'copy';

/** A choice resolved against the tip — `route` is null for an estimate. */
export type ChosenAlternative = Omit<
  TransportAlternativeRouteOption,
  'route'
> & {
  route: TransportAlternativeRoute | null;
};

/** Where the confirmed itineraries run. */
export interface TransportAlternativeRoutes {
  origin: string;
  destination: string;
}

export interface TransportAlternativeRoute {
  /** One-way distance of the itinerary in km. */
  distanceKm: number;
  /** One-way door-to-door duration in hours. */
  durationHours: number;
  /**
   * The vehicle covering the itinerary's longest stretch — `highspeedTrain`,
   * `coachBus`, `ferryPassengerOnly`…
   *
   * Not what the alternative should be *labelled*: the longest leg of a `mixed`
   * itinerary is often a TGV, and calling that row "Train" is the confusion
   * `mixed` exists to end. The mode it sits under names it; this only says which
   * vehicle dominates.
   */
  transportType: string;
  /** Vehicle changes along the way: 0 for a direct service. */
  changes: number;
  /** Each vehicle taken, in order. Empty for a road itinerary. */
  legs: TransportAlternativeRouteLeg[];
  /** Deep link reopening the itinerary in Google Maps. */
  mapsUrl: string;
}

/**
 * One vehicle of a confirmed itinerary, as a traveller reads it on a departure
 * board. No clock times: the API times a route from the moment it asks, and the
 * trip is planned for another day.
 */
export interface TransportAlternativeRouteLeg {
  transportType: string;
  /**
   * Google's own vehicle wording: "High-speed train", "Bus".
   *
   * Always English, whatever the user's language: the API queries Directions
   * with `language: 'en'` so a single cache entry serves every locale. Prefer
   * `line` for display and fall back on this only when it is empty.
   */
  vehicleName: string;
  /** The line as shown on the board: "TGV INOUI", "FlixBus", "9741". */
  line: string;
  /** Where the vehicle is headed, as written on it. */
  headsign: string | null;
  operator: string | null;
  /** The operator's site — where the journey is actually booked. */
  operatorUrl: string | null;
  departureStop: string;
  arrivalStop: string;
  durationHours: number;
  distanceKm: number;
  /** Stops served between boarding and alighting. */
  stops: number;
  /**
   * Whether this vehicle carries the journey rather than feeds it.
   *
   * False for an access leg — Google anchors an itinerary on the city, so a
   * coach out of Brussels opens with a train to Bruxelles-Nord. The API leaves
   * access legs out of the emissions, so they are shown apart and never become
   * transports of their own when the tip is applied.
   */
  isLineHaul: boolean;
}

export interface DirectFlightTipData {
  transportId: string;
  transportType: string;
  flightRoute: string;
  currentEmissions: number;
  directEmissions: number;
  savings: number;
  savingsPercent: number;
  multiStopKm: number;
  directKm: number;
}

/**
 * A longer stay and the eco-score it reaches. The intensity is in kg CO₂e per
 * day and per traveller.
 */
export interface TripDurationOption {
  days: number;
  intensity: number;
  ecoScore: number;
  /**
   * kg CO₂e the extra days add to the trip, all travellers together — a
   * longer stay lowers the intensity, not the footprint.
   */
  additionalImpact: number;
}

/**
 * The longer stays that raise the trip's eco-score, chosen by the API.
 * Intensities are in kg CO₂e per day and per traveller.
 */
export interface TripDurationTipData {
  currentDays: number;
  currentIntensity: number;
  currentEcoScore: number;
  /**
   * For each eco-score gained, the shortest stay that reaches it, shortest
   * first — every option scores higher than the one before. Never empty.
   */
  options: TripDurationOption[];
}

export interface EcoAccommodationTipData {
  accommodationId: string;
  accommodationTitle?: string;
  accommodationType: string;
  currentEmissions: number;
  ecoEmissions: number;
  savings: number;
  savingsPercent: number;
}

export interface HighEmissionActivityTipData {
  activityId: string;
  activityTitle?: string;
  activityType: string;
  impact: number;
  impactPerPerson: number;
}
